import { useFormikContext } from "formik";
import { useTranslation } from "react-i18next";
import { useCallback, useState } from "react";
import HeroButton from "/app/src/components/HeroUi/Button";
import HeroSelect from "/app/src/components/HeroUi/Select";
import { InputNumber, Row, Col } from "antd";

interface FormValues {
  value: string | undefined;
}

/**
 *
 * @returns DynamicButtons component
 */
export default function DynamicButtons({
  disabled = false,
  dataPull = false,
}: {
  disabled?: boolean;
  dataPull?: boolean;
}) {
  const { t } = useTranslation();

  const { setFieldValue, values } = useFormikContext<FormValues>();
  const [period, setPeriod] = useState<string>("last");
  const [quantity, setQuantity] = useState<number>(1);
  const [frequency, setFrequency] = useState<string>("day");
  const [dateTimeFormat, setDateTimeFormat] = useState<string>("%Y%m%d.0");

  const updateDateTimeFormat = useCallback(
    (e) => {
      setDateTimeFormat(e.target.value);
    },
    [setDateTimeFormat],
  );

  const updateQuantity = useCallback(
    (value: number) => {
      setQuantity(value);
    },
    [setQuantity],
  );

  const updatePeriod = useCallback(
    (e) => {
      setPeriod(e.target.value);
    },
    [setPeriod],
  );

  const updateFrequency = useCallback(
    (e) => {
      setFrequency(e.target.value);
    },
    [setFrequency],
  );

  const addField = useCallback(() => {
    const currentValue = values.value ?? "";
    if (dataPull) {
      setFieldValue(
        "path",
        `${currentValue}{${period}${quantity}${frequency}:${dateTimeFormat}}`,
      );
    } else {
      setFieldValue(
        "value",
        `${currentValue}{${period}${quantity}${frequency}:${dateTimeFormat}}`,
      );
    }
  }, [
    values.value,
    dataPull,
    setFieldValue,
    period,
    quantity,
    frequency,
    dateTimeFormat,
  ]);

  return (
    <div className="mt-2">
      <Row gutter={16}>
        <Col span={4}>
          <HeroSelect
            value={period}
            defaultSelectedKeys={["last"]}
            onChange={updatePeriod}
            items={[
              { key: "last", label: "Last", value: "last" },
              { key: "next", label: "Next", value: "next" },
            ]}
            ariaLabel="Select period"
          />
        </Col>
        <Col span={2}>
          <InputNumber
            min={1}
            max={100}
            value={quantity}
            size="large"
            onChange={updateQuantity}
          />
        </Col>
        <Col span={4}>
          <HeroSelect
            value={frequency}
            defaultSelectedKeys={["day"]}
            onChange={updateFrequency}
            items={[
              { key: "day", label: "Day(s)", value: "day" },
              { key: "week", label: "Week(s)", value: "week" },
              { key: "month", label: "Month(s)", value: "month" },
            ]}
            ariaLabel="Select period"
          />
        </Col>
        <Col span={8}>
          <HeroSelect
            value={dateTimeFormat}
            defaultSelectedKeys={["%Y%m%d.0"]}
            onChange={updateDateTimeFormat}
            ariaLabel="Select date time format"
            items={[
              { label: "YYYYMMDD.0", value: "%Y%m%d.0", key: "%Y%m%d.0" },
              { label: "MM/DD/YYYY", value: "%m/%d/%Y", key: "%m/%d/%Y" },
              { label: "DD/MM/YYYY", value: "%d/%m/%Y", key: "%d/%m/%Y" },
              { label: "YYYY-MM-DD", value: "%Y-%m-%d", key: "%Y-%m-%d" },
              {
                label: "YYYY-MM-DD HH:MM:SS",
                value: "%Y-%m-%d %H:%M:%S",
                key: "%Y-%m-%d %H:%M:%S",
              },
              {
                label: "YYYY-MM-DDTHH:MM:SS.SSS",
                value: "%Y-%m-%d%H:%M:%S.%f",
                key: "%Y-%m-%d%H:%M:%S.%f",
              },
              {
                label: "YYYY-MM-DDTHH:MM:SSZ",
                value: "%Y-%m-%dT%H:%M:%SZ",
                key: "%Y-%m-%dT%H:%M:%SZ",
              },
              {
                label: "YYYY-MM-DD HH:MM:SS.SSSZ",
                value: "%Y-%m-%d %H:%M:%S.%fZ",
                key: "%Y-%m-%d %H:%M:%S.%fZ",
              },
              {
                value: "%Y-%m-%dT%H:%M:%S%z",
                label: "YYYY-MM-DDTHH:MM:SS+HH:MM",
                key: "%Y-%m-%dT%H:%M:%S%z",
              },
              { label: "Epoch (seconds)", value: "epoch", key: "epoch" },
              {
                label: "Epoch (milliseconds)",
                value: "epoch_ms",
                key: "epoch_ms",
              },
            ]}
          />
        </Col>
        <Col span={6}>
          <HeroButton
            isDisabled={disabled}
            onClick={addField}
            size="md"
            fullWidth
          >
            {t("translation:add")}
          </HeroButton>
        </Col>
      </Row>
    </div>
  );
}
